import { useState, useEffect, useRef } from "react";

const C = {
  surface:"#181818", surface2:"#202020", border:"#2a2a2a",
  muted:"#666", orange:"#FF5F1F", magenta:"#E8197D",
  gold:"#F5A623", green:"#4ade80", white:"#fff", red:"#f87171", blue:"#60a5fa",
};

const inp = (x={}) => ({ background:C.surface2, border:`0.5px solid ${C.border}`, borderRadius:8, padding:"9px 12px", color:C.white, fontSize:13, outline:"none", width:"100%", boxSizing:"border-box", ...x });
const btn = (v="primary",x={}) => ({ padding:"8px 16px", borderRadius:8, fontSize:13, fontWeight:500, cursor:"pointer", border:"none", boxSizing:"border-box", ...(v==="primary"?{background:`linear-gradient(135deg,${C.orange},${C.magenta})`,color:C.white}:{}), ...(v==="ghost"?{background:"none",border:`0.5px solid ${C.border}`,color:C.muted}:{}), ...x });

export const RUNDOWN_TYPES = {
  rehearsal:{ label:"Prøver",   color:C.magenta },
  show:     { label:"Show",    color:C.orange  },
  break:    { label:"Pause",   color:C.gold    },
  info:     { label:"Info",    color:"#888"    },
  travel:   { label:"Reise",   color:C.blue    },
};

// Regn ut varighet mellom to tidspunkter (over midnatt støttes)
function calcDuration(from, to) {
  if(!from || !to) return null;
  const [fh,fm] = from.split(":").map(Number);
  const [th,tm] = to.split(":").map(Number);
  let diff = (th*60+tm) - (fh*60+fm);
  if(diff < 0) diff += 24*60;
  if(diff === 0) return null;
  const h = Math.floor(diff/60);
  const m = diff%60;
  return h>0 ? `${h}t ${m>0?m+"min":""}`.trim() : `${m}min`;
}

// Er denne raden aktiv nå?
function isNowActive(r, eventDate) {
  const now = new Date();
  const today = now.toISOString().slice(0,10);
  const rowDate = r.date || eventDate || today;
  if(rowDate !== today) return false;
  const [fh,fm] = r.time.split(":").map(Number);
  const rowStart = new Date(); rowStart.setHours(fh,fm,0,0);
  if(!r.timeTo) return now >= rowStart && now < new Date(rowStart.getTime()+60*60*1000);
  const [th,tm] = r.timeTo.split(":").map(Number);
  const rowEnd = new Date(); rowEnd.setHours(th,tm,0,0);
  if(rowEnd <= rowStart) rowEnd.setDate(rowEnd.getDate()+1);
  return now >= rowStart && now < rowEnd;
}

function fmtDate(date) {
  return new Date(date+"T12:00:00").toLocaleDateString("nb-NO",{day:"numeric",month:"short"});
}

// ── Enkelt rad i visningsmodusen ──
function RunDownRow({ r, eventDate, onClick, isActive, expanded, showProgramItem, teamUsers=[], isMobile }) {
  const type = RUNDOWN_TYPES[r.type] || { label:r.type, color:C.muted };
  const duration = calcDuration(r.time, r.timeTo);
  const hasMore = !!(r.notes || showProgramItem);

  return (
    <div
      onClick={hasMore ? onClick : undefined}
      style={{
        padding:"12px 14px",
        background: isActive ? `${C.orange}15` : C.surface2,
        borderRadius:8, borderLeft:`3px solid ${isActive ? C.orange : type.color}`,
        cursor: hasMore ? "pointer" : "default",
        marginBottom:6,
        boxShadow: isActive ? `0 0 0 1px ${C.orange}44` : "none",
        transition:"all 0.2s",
      }}
    >
      {/* Hovedlinje */}
      <div style={{display:"flex", alignItems:"center", gap:10}}>
        {/* Tid */}
        <div style={{width:48, flexShrink:0}}>
          <div style={{fontFamily:"monospace", fontSize:14, fontWeight:600, color: isActive ? C.orange : C.white}}>{r.time}</div>
          {r.timeTo && <div style={{fontFamily:"monospace", fontSize:11, color:C.muted}}>{r.timeTo}</div>}
        </div>

        {/* Type badge – skjul på mobil */}
        {!isMobile && <div style={{width:70, flexShrink:0, fontSize:11, padding:"2px 7px", borderRadius:20, background:`${type.color}22`, color:type.color, border:`0.5px solid ${type.color}44`, whiteSpace:"nowrap", textAlign:"center"}}>
          {type.label}
        </div>}

        {/* Tittel */}
        <div style={{flex:1, minWidth:0}}>
          <div style={{fontSize:13, fontWeight: (isActive||r.type==="show")?600:400, color:C.white, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{r.title}</div>
          {r.date && r.date !== eventDate && <div style={{fontSize:11, color:C.gold, marginTop:2}}>{fmtDate(r.date)}</div>}
        </div>

        {/* Varighet */}
        {duration && <div style={{fontSize:11, color:C.muted, flexShrink:0}}>{duration}</div>}

        {/* Nå-indikator */}
        {isActive && <div style={{width:8, height:8, borderRadius:"50%", background:C.orange, flexShrink:0, boxShadow:`0 0 6px ${C.orange}`}}/>}

        {/* Ekspander-pil – kun hvis det finnes mer info */}
        {hasMore && <div style={{fontSize:10, color:C.muted, flexShrink:0, transition:"transform 0.2s", transform:expanded?"rotate(180deg)":"rotate(0deg)"}}>▾</div>}
      </div>

      {/* Ekspandert innhold */}
      {expanded && (
        <div style={{marginTop:12, paddingTop:12, borderTop:`0.5px solid ${C.border}`}}>
          {showProgramItem && (
            <div style={{marginBottom:10, display:"flex", flexDirection:"column", gap:6}}>
              {showProgramItem.persons && showProgramItem.persons.length > 0 && (
                <div>
                  <div style={{fontSize:11, color:C.muted, textTransform:"uppercase", letterSpacing:0.5, marginBottom:4}}>Hvem er med</div>
                  <div style={{display:"flex", flexWrap:"wrap", gap:6}}>
                    {showProgramItem.persons.map((id,i) => {
                      const u = teamUsers.find(x=>x.id===id);
                      const name = u ? u.name.split(" ")[0] : id;
                      const roleColors = {danser:"#E8197D",vokalist:"#a78bfa",tm:"#F5A623",akrobat:"#FF5F1F",musiker:"#60a5fa",tekniker:"#94a3b8",admin:"#34d399"};
                      const color = u ? (roleColors[u.role]||C.orange) : C.orange;
                      return <div key={i} style={{fontSize:12, padding:"2px 8px", borderRadius:20, background:`${color}22`, color, border:`0.5px solid ${color}44`}}>{name}</div>;
                    })}
                  </div>
                </div>
              )}
              {showProgramItem.costume && (
                <div>
                  <div style={{fontSize:11, color:C.muted, textTransform:"uppercase", letterSpacing:0.5, marginBottom:2}}>Kostyme</div>
                  <div style={{fontSize:13, color:"#ccc"}}>{showProgramItem.costume}</div>
                </div>
              )}
              {showProgramItem.hair_makeup && (
                <div>
                  <div style={{fontSize:11, color:C.muted, textTransform:"uppercase", letterSpacing:0.5, marginBottom:2}}>Hår og sminke</div>
                  <div style={{fontSize:13, color:"#ccc"}}>{showProgramItem.hair_makeup}</div>
                </div>
              )}
              {showProgramItem.notes && (
                <div>
                  <div style={{fontSize:11, color:C.muted, textTransform:"uppercase", letterSpacing:0.5, marginBottom:2}}>Notater</div>
                  <div style={{fontSize:13, color:"#ccc", lineHeight:1.6, whiteSpace:"pre-wrap"}}>{showProgramItem.notes}</div>
                </div>
              )}
            </div>
          )}
          {r.notes
            ? <div style={{fontSize:13, color:"#ccc", lineHeight:1.6, whiteSpace:"pre-wrap"}}>{r.notes}</div>
            : !showProgramItem && <div style={{fontSize:13, color:"#444"}}>Ingen tilleggsinformasjon.</div>
          }
          {duration && <div style={{fontSize:11, color:C.muted, marginTop:8}}>⏱ Varighet: {duration}</div>}
        </div>
      )}
    </div>
  );
}



// ── Redigeringsskjema for én rad ──
function RunDownForm({ initial, eventDate, onSave, onCancel, showProgramItems = [] }) {
  const [date,    setDate]    = useState(initial?.date    || eventDate || "");
  const [time,    setTime]    = useState(initial?.time    || "");
  const [timeTo,  setTimeTo]  = useState(initial?.timeTo  || "");
  const [type,    setType]    = useState(initial?.type    || "info");
  const [title,   setTitle]   = useState(initial?.title   || "");
  const [notes,   setNotes]   = useState(initial?.notes   || "");
  const [showTimeTo, setShowTimeTo] = useState(!!(initial?.timeTo));
  const [showProgramRef, setShowProgramRef] = useState(initial?.showProgramRef || "");

  const duration = calcDuration(time, timeTo);

  const save = () => {
    if(!time || !title.trim()) return;
    onSave({
      ...(initial||{}),
      id: initial?.id || Date.now(),
      date, time, timeTo: showTimeTo ? timeTo : "",
      type, title:title.trim(), notes:notes.trim(),
      showProgramRef: type === "show" ? showProgramRef : "",
    });
  };

  return (
    <div style={{padding:12, background:C.surface2, borderRadius:8, border:`0.5px solid ${C.orange}44`, marginBottom:8}}>
      {/* Linje 1: Dato, Fra, Til, Type */}
      <div style={{display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:8, marginBottom:8}}>
        <div>
          <div style={{fontSize:11,color:C.muted,marginBottom:4,textTransform:"uppercase",letterSpacing:0.5}}>Dato</div>
          <input style={inp({height:44,boxSizing:"border-box"})} type="date" value={date} onChange={e=>setDate(e.target.value)}/>
        </div>
        <div>
          <div style={{fontSize:11,color:C.muted,marginBottom:4,textTransform:"uppercase",letterSpacing:0.5}}>Fra</div>
          <input style={inp({height:44,boxSizing:"border-box"})} type="time" value={time} onChange={e=>setTime(e.target.value)}/>
        </div>
        <div>
          <div style={{fontSize:11,color:C.muted,marginBottom:4,textTransform:"uppercase",letterSpacing:0.5}}>Til (valgfritt)</div>
          <input
            style={inp({height:44,boxSizing:"border-box"})}
            type="time"
            value={timeTo}
            onChange={e=>{ setTimeTo(e.target.value); if(e.target.value) setShowTimeTo(true); else setShowTimeTo(false); }}
            placeholder="--:--"
          />
        </div>
        <div>
          <div style={{fontSize:11,color:C.muted,marginBottom:4,textTransform:"uppercase",letterSpacing:0.5}}>Type</div>
          <select style={inp({height:44,boxSizing:"border-box"})} value={type} onChange={e=>setType(e.target.value)}>
            {Object.entries(RUNDOWN_TYPES).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
      </div>

      {/* Linje 2: Show-program kobling (kun når type=show) */}
      {type === "show" && showProgramItems.length > 0 && (
        <div style={{marginBottom:8}}>
          <div style={{fontSize:11,color:C.muted,marginBottom:4,textTransform:"uppercase",letterSpacing:0.5}}>Koble til show-nummer</div>
          <select
            style={inp({height:44,boxSizing:"border-box"})}
            value={showProgramRef}
            onChange={e => {
              const id = e.target.value;
              setShowProgramRef(id);
              const item = showProgramItems.find(s => String(s.id) === String(id));
              if(item) setTitle(item.title || "");
            }}
          >
            <option value="">– Velg show-nummer –</option>
            {showProgramItems.map(s => (
              <option key={s.id} value={s.id}>{s.order ? `${s.order}. ` : ""}{s.title}</option>
            ))}
          </select>
        </div>
      )}

      {/* Linje 2: Beskrivelse + varighet */}
      <div style={{marginBottom:8}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
          <div style={{fontSize:11,color:C.muted,textTransform:"uppercase",letterSpacing:0.5}}>Beskrivelse</div>
          {duration&&<div style={{fontSize:11,color:C.green}}>⏱ {duration}</div>}
        </div>
        <input style={inp({height:44,boxSizing:"border-box"})} placeholder="Hva skjer?" value={title} onChange={e=>setTitle(e.target.value)} onKeyDown={e=>e.key==="Enter"&&save()}/>
      </div>

      {/* Linje 3: Notater */}
      <div style={{marginBottom:10}}>
        <div style={{fontSize:11,color:C.muted,marginBottom:4,textTransform:"uppercase",letterSpacing:0.5}}>Notater (valgfritt)</div>
        <textarea style={inp({minHeight:60,resize:"vertical"})} placeholder="Ekstra info som vises når man trykker på raden..." value={notes} onChange={e=>setNotes(e.target.value)}/>
      </div>

      <div style={{display:"flex",gap:8}}>
        <button style={btn("ghost",{flex:1})} onClick={onCancel}>Avbryt</button>
        <button style={btn("primary",{flex:1})} onClick={save}>Lagre</button>
      </div>
    </div>
  );
}

// ── Hoved-komponent ──
export function RunDownEditor({ runsheet, onChange, eventDate, showProgramItems = [] }) {
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const sortKey = r => (r.date||eventDate||"9999-99-99") + "T" + (r.time||"00:00");

  const save = (row) => {
    const exists = runsheet.find(r=>r.id===row.id);
    const updated = exists
      ? runsheet.map(r=>r.id===row.id?row:r)
      : [...runsheet, row];
    onChange(updated.sort((a,b)=>sortKey(a).localeCompare(sortKey(b))));
    setEditingId(null);
    setShowAddForm(false);
  };

  const remove = (id) => onChange(runsheet.filter(r=>r.id!==id));

  return (
    <div>
      {runsheet.map(r=>(
        <div key={r.id}>
          {editingId===r.id
            ? <RunDownForm initial={r} eventDate={eventDate} onSave={save} onCancel={()=>setEditingId(null)} showProgramItems={showProgramItems}/>
            : <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
                <div style={{flex:1}}>
                  <RunDownRow r={r} eventDate={eventDate} isActive={false}/>
                </div>
                <button onClick={()=>setEditingId(r.id)} style={{background:"none",border:"none",color:"#555",cursor:"pointer",fontSize:13,flexShrink:0,padding:"0 4px"}}>✎</button>
                <button onClick={()=>remove(r.id)} style={{background:"none",border:"none",color:"#555",cursor:"pointer",fontSize:18,flexShrink:0,padding:"0 4px"}}>×</button>
              </div>
          }
        </div>
      ))}
      {showAddForm
        ? <RunDownForm eventDate={eventDate} onSave={save} onCancel={()=>setShowAddForm(false)} showProgramItems={showProgramItems}/>
        : <button style={{...btn("ghost"),width:"100%",marginTop:8}} onClick={()=>setShowAddForm(true)}>+ Legg til rad</button>
      }
    </div>
  );
}

// ── Visningsmodusen (ikke-redigerbar, med nå-indikator og inline ekspandering) ──
export function RunDownViewer({ runsheet, eventDate, isMobile, showProgramItems = [], teamUsers = [] }) {
  const [expandedId, setExpandedId] = useState(null);
  const [, setTick] = useState(0);
  const nowRef = useRef(null);

  // Oppdater hvert minutt for nå-indikatoren
  useEffect(()=>{
    const interval = setInterval(()=>setTick(t=>t+1), 60000);
    return ()=>clearInterval(interval);
  },[]);

  // Scroll til aktiv rad
  useEffect(()=>{
    if(nowRef.current) nowRef.current.scrollIntoView({behavior:"smooth", block:"center"});
  },[]);

  if(!runsheet||runsheet.length===0) return <div style={{fontSize:13,color:"#444"}}>Ingen kjøreplan enda.</div>;

  return (
    <div>
      {runsheet.map(r=>{
        const active = isNowActive(r, eventDate);
        const expanded = expandedId === r.id;
        return (
          <div key={r.id} ref={active?nowRef:null}>
            <RunDownRow
              r={r}
              eventDate={eventDate}
              isActive={active}
              expanded={expanded}
              onClick={()=>setExpandedId(expanded ? null : r.id)}
              showProgramItem={r.showProgramRef ? showProgramItems.find(s => String(s.id) === String(r.showProgramRef)) : null}
              teamUsers={teamUsers}
              isMobile={isMobile}
            />
          </div>
        );
      })}
    </div>
  );
}